import { getOrchestratorRootUrl } from '../orchestratorBaseUrl';
import { getOrCreateWebPaymentGuestId } from '../utils/webPaymentGuest';

/**
 * Credencial con la que esta persona puede consultar SUS reservas.
 *
 * Antes, "mis reservas" se pedía poniendo el email en la URL, y ese email salía de
 * `?email=` de la query string. Cualquiera podía escribir el de otra persona y leer su
 * historial completo (B2-1 de la auditoría).
 *
 * Ahora la identidad la decide el servidor: el orquestador acredita a la persona (sesión
 * de Clerk verificada contra JWKS, initData de Telegram con HMAC, token firmado de
 * WhatsApp o el guestId de este navegador) y devuelve tanto el token como la identidad con
 * la que se puede consultar. El navegador ya no propone quién es: lo recibe.
 */
export interface CustomerCredential {
  token: string | null;
  identity: {
    email: string | null;
    telegramId: string | null;
    whatsappId: string | null;
  };
}

const EMPTY: CustomerCredential = {
  token: null,
  identity: { email: null, telegramId: null, whatsappId: null },
};

/** Cache en memoria por tenant: el token dura 30 min y la página lo pide en cada montaje. */
const cache = new Map<string, { value: CustomerCredential; expiresAt: number }>();

export function clearCustomerCredentialCache(): void {
  cache.clear();
}

export async function fetchCustomerCredential(params: {
  tenantId: string;
  /** JWT de Clerk si hay sesión. El servidor verifica la firma; no se confía en él aquí. */
  clerkToken?: string | null;
  /** initData crudo de la Mini App de Telegram, si estamos dentro de Telegram. */
  telegramInitData?: string | null;
  botId?: string | null;
}): Promise<CustomerCredential> {
  const { tenantId, clerkToken, telegramInitData, botId } = params;
  if (!tenantId) return EMPTY;

  /**
   * ⚠️ LA CLAVE INCLUYE CON QUÉ SE PIDIÓ, no solo el tenant.
   *
   * Con la clave solo por tenant, el primero que llegara mandaba: una pantalla que pide la
   * credencial sin sesión —solo con el `guestId` de este navegador— dejaba cacheada una
   * credencial débil, y durante 30 minutos la usaba también la pantalla que sí tenía sesión.
   * No es una fuga: el token acredita a quien acredita, y pedir el email de otro devuelve 403.
   * Es peor de otra manera — quien ha iniciado sesión ve un error en sus propias reservas y no
   * hay nada en pantalla que explique por qué.
   *
   * Mientras booking-service estuvo en `permissive` esto no se notaba: contestaba sin token.
   * Al cerrarlo el 5-sep-2026 dejó de ser inofensivo.
   */
  const claveCache = `${tenantId}|${clerkToken ? 'c' : ''}${telegramInitData ? 't' : ''}`;
  const cached = cache.get(claveCache);
  if (cached && cached.expiresAt > Date.now()) return cached.value;

  const q = new URLSearchParams({ tenant_id: tenantId });
  const guestId = getOrCreateWebPaymentGuestId(tenantId);
  if (guestId) q.set('guest_id', guestId);
  if (botId) q.set('bot_id', botId);

  const headers: Record<string, string> = {};
  if (clerkToken) headers.Authorization = `Bearer ${clerkToken}`;
  if (telegramInitData) headers['X-Telegram-Init-Data'] = telegramInitData;

  const res = await fetch(
    `${getOrchestratorRootUrl()}/api/v1/web/booking/customer-token?${q.toString()}`,
    { headers },
  );

  // 401 = no se puede acreditar a nadie en este navegador. No es un error a mostrar: es
  // "no hay nada que enseñarte todavía". Quien llama decide el mensaje.
  if (!res.ok) return EMPTY;

  const data = await res.json();
  const value: CustomerCredential = {
    token: data?.token ?? null,
    identity: {
      email: data?.identity?.email ?? null,
      telegramId: data?.identity?.telegram_id ?? null,
      whatsappId: data?.identity?.whatsapp_id ?? null,
    },
  };

  // Margen de 60 s para no usar un token que caduca a mitad de la petición.
  const ttlMs = Math.max(0, ((data?.expires_in_sec ?? 1800) - 60) * 1000);
  if (value.token && ttlMs > 0) {
    cache.set(claveCache, { value, expiresAt: Date.now() + ttlMs });
  }
  return value;
}

/** Cabecera que espera CustomerIdentityGuard de booking-service. */
export function customerTokenHeaders(token: string | null): Record<string, string> {
  return token ? { 'X-Customer-Token': token } : {};
}
